import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import IconButton from '@material-ui/core/IconButton'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import CheckBoxIcon from '@material-ui/icons/CheckBoxOutlined'
import CheckBoxFilledIcon from '@material-ui/icons/CheckBox'
import CloudUploadIcon from '@material-ui/icons/CloudUploadOutlined'
import AddPhotoAlternateOutlinedIcon from '@material-ui/icons/AddPhotoAlternateOutlined'
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined'
import PictureAsPdfOutlinedIcon from '@material-ui/icons/PictureAsPdfOutlined'
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled'
import CircularProgress from '@material-ui/core/CircularProgress'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import classnames from 'classnames'
import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc'
import type { SortableContainerProps, SortableElementProps } from 'react-sortable-hoc'
import type { QuoteFile } from 'paintscout'
import FileViewerButton from './FileViewerButton'
import CloudinaryImage from '../CloudinaryImage'
import { isImageFile, isPdfFile, isVideoFile, getS3VideoUrl, withVideoFirstFrame } from './file-type-utils'
import { imageCrossOrigin, retryCorsImageOnError } from '../../util'
import { useTranslation } from '../../providers/TranslationProvider'

export type FileViewerGalleryFile = QuoteFile & { error?: string }

export interface FileViewerGalleryProps {
  files: FileViewerGalleryFile[]
  selectedKey?: string
  onSelect: (file: FileViewerGalleryFile) => void
  onSelectAll?: () => void
  onUploadClick?: () => void
  /**
   * When supplied, renders a second "add" affordance beside the upload tile that
   * reopens a provider's own picker (e.g. CompanyCam) so more provider photos can
   * be added without leaving the viewer. The wording comes from `addProviderLabel`
   * to keep this component integration-agnostic.
   */
  onAddProvider?: () => void
  /** Label/tooltip for the `onAddProvider` affordance, e.g. "Add from CompanyCam". */
  addProviderLabel?: string
  /**
   * When supplied, thumbnails become drag-to-reorder. Called with the full file
   * list in its new order. Reorder is suppressed while in multi-select mode so
   * checkbox toggling isn't hijacked by a drag.
   */
  onReorder?: (files: FileViewerGalleryFile[]) => void
  /**
   * Multi-select state. When non-empty, the gallery enters multi-select mode:
   * thumbs render a checkbox overlay and clicks toggle selection instead of
   * navigating the preview.
   */
  selectedKeys?: Set<string>
  /**
   * Toggle a file's membership in the multi-select set. When omitted, the
   * gallery stays in single-preview mode regardless of `selectedKeys`.
   */
  onToggleSelect?: (file: FileViewerGalleryFile) => void
  /**
   * Width below which the gallery switches to single-column compact mode
   * and the "Select all" / "Upload" actions collapse into a kebab menu.
   * Ignored when `orientation === 'horizontal'`.
   */
  compactThreshold?: number
  /**
   * 'vertical' (default) renders a 2-column grid suitable for a sidebar.
   * 'horizontal' renders a single-row strip of thumbnails for mobile layouts.
   */
  orientation?: 'vertical' | 'horizontal'
}

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: theme.palette.common.white
  },
  rootHorizontal: {
    height: 'auto'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2, 1, 2),
    minHeight: 44
  },
  title: {
    ...theme.typography.subtitle1,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.paintscout.palette.brand.Neutral.P30
  },
  scroll: {
    flex: 1,
    overflowY: 'auto',
    // small top padding so the selected thumb's box-shadow ring isn't clipped on the first row
    padding: theme.spacing(0.25, 2, 2, 2)
  },
  scrollHorizontal: {
    flex: 'none',
    overflowX: 'auto',
    overflowY: 'hidden',
    padding: theme.spacing(0.25, 2, 1.5, 2)
  },
  grid: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  gridCompact: {
    gridTemplateColumns: '1fr'
  },
  strip: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    justifyContent: 'flex-start'
  },
  headerActions: {
    display: 'inline-flex',
    gap: theme.spacing(1)
  },
  horizontalActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    // Single row that scrolls horizontally on narrow phones instead of wrapping
    // or clipping. With both "Add from CompanyCam" and "Upload a file" present the
    // three nowrap buttons exceed the viewport width (#21231); keeping them on one
    // scrollable line (Select all + Upload first, CompanyCam last) means every
    // button stays reachable. Mirrors the horizontally-scrolling thumbnail strip.
    flexWrap: 'nowrap',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: theme.spacing(1, 2, 1.5, 2),
    // Buttons keep their size; the row scrolls rather than squishing them.
    '& > *': {
      flexShrink: 0
    }
  },
  thumb: {
    position: 'relative',
    aspectRatio: '1 / 1',
    width: '100%',
    padding: 0,
    // iPad lifts a preview of the tapped element when the upload tile opens the
    // native file picker; suppress it (#20634).
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none',
    WebkitUserSelect: 'none',
    // Suppress native image drag / text selection so the press gesture reaches
    // react-sortable-hoc instead of starting a native image drag that fights the
    // reorder and leaves a ghost stuck to the cursor (#21264; mirrors the
    // FilesGridNext fix for #20637).
    userSelect: 'none',
    '& img': {
      WebkitUserDrag: 'none',
      userSelect: 'none'
    },
    background: theme.paintscout.palette.brand.Neutral.P96,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderRadius: theme.borderRadius.lg,
    cursor: 'pointer',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.paintscout.palette.brand.Neutral.P50,
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      borderColor: theme.paintscout.palette.brand.Neutral.P70
    }
  },
  stripThumb: {
    flex: '0 0 72px',
    width: 72,
    minWidth: 72,
    height: 72,
    aspectRatio: 'unset'
  },
  thumbSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
  },
  thumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  // Centered play badge overlaid on a video thumbnail's poster/first frame.
  videoOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    color: theme.palette.common.white,
    '& svg': {
      fontSize: 40,
      filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.5))'
    }
  },
  pdfPreviewSlot: {
    flex: '1 1 auto',
    minHeight: 0,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    marginTop: theme.spacing(1),
    marginBottom: `-${theme.spacing(0.5)}px`,
    '& img': {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain'
    }
  },
  pdfPreviewSlotHorizontal: {
    marginTop: 0,
    marginBottom: 0,
    padding: theme.spacing(0.5)
  },
  pdfTitle: {
    textAlign: 'left',
    color: theme.paintscout.palette.brand.Neutral.P50
  },
  pdfBadge: {
    alignSelf: 'flex-start',
    paddingLeft: theme.spacing(1),
    color: theme.paintscout.palette.brand.Neutral.P50
  },
  docTitle: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P30,
    padding: theme.spacing(0, 1),
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    marginTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5)
  },
  docFormat: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P50,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5)
  },
  uploadTile: {
    border: `1px dashed ${theme.paintscout.palette.brand.Neutral.P85}`,
    background: theme.palette.common.white,
    color: theme.paintscout.palette.brand.Neutral.P50,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main
    }
  },
  uploadIcon: {
    fontSize: 28
  },
  uploadLabel: {
    ...theme.typography.caption,
    marginTop: theme.spacing(0.5)
  },
  menuItemIcon: {
    minWidth: 32
  },
  checkboxOverlay: {
    position: 'absolute',
    top: theme.spacing(0.75),
    right: theme.spacing(0.75),
    width: 22,
    height: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    background: theme.palette.common.white,
    color: theme.palette.primary.main,
    pointerEvents: 'none',
    boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
    '& svg': {
      fontSize: 22
    }
  },
  spinnerOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255, 255, 255, 0.6)'
  },
  errorOverlay: {
    color: theme.palette.error.main
  },
  // Applied to the floating clone while dragging. The clone is appended to
  // <body>, so it needs a z-index above the dialog to stay visible (matches
  // FilesGridNext).
  dragging: {
    zIndex: 2147483002,
    cursor: 'grabbing'
  }
}))

interface ThumbButtonProps extends SortableElementProps {
  file: FileViewerGalleryFile
  isHorizontal: boolean
  selected: boolean
  inMultiSelectMode: boolean
  isMultiSelected: boolean
  onClick: () => void
}

/**
 * A single gallery thumbnail. Extracted so it can be wrapped by
 * `SortableElement` for drag-to-reorder while keeping the visual identical to
 * the original inline render. Re-calls `useStyles` (cached by makeStyles) so it
 * shares the gallery's styles without threading `classes` through props.
 */
function ThumbButton({ file, isHorizontal, selected, inMultiSelectMode, isMultiSelected, onClick }: ThumbButtonProps) {
  const classes = useStyles({})
  const { __ } = useTranslation()
  const isImage = isImageFile(file)
  const isPdf = isPdfFile(file)
  const isVideo = isVideoFile(file)
  const uploading = file.isUploading
  const errored = !!file.error

  return (
    <button
      type="button"
      className={classnames(classes.thumb, isHorizontal && classes.stripThumb, selected && classes.thumbSelected)}
      onClick={onClick}
    >
      {inMultiSelectMode && isMultiSelected && (
        <div className={classes.checkboxOverlay}>
          <CheckBoxFilledIcon fontSize="small" />
        </div>
      )}
      {isImage && file.src ? (
        // crossOrigin keeps the cached response CORS-clean so the
        // editor (which loads the same src with CORS) gets a usable
        // cache hit instead of a poisoned non-CORS entry on Safari.
        // retryCorsImageOnError self-heals an already-poisoned entry.
        <img
          // draggable={false} stops Firefox starting a native image drag
          // (it ignores the WebkitUserDrag CSS); together they keep the press
          // gesture with react-sortable-hoc instead (#21264).
          draggable={false}
          crossOrigin={imageCrossOrigin(file)}
          src={file.src}
          alt={file.title || ''}
          className={classes.thumbImg}
          onError={retryCorsImageOnError}
        />
      ) : isVideo ? (
        <>
          {file.src && file.s3PublicKey ? (
            // Native <video> with preload=metadata renders the first frame as a
            // poster without needing to derive a thumbnail URL. The `#t=0.001`
            // fragment (withVideoFirstFrame) nudges iOS/iPadOS WebKit to actually
            // paint that frame instead of a grey box (#21623). muted/playsInline
            // keep it inert in the strip. draggable={false} keeps the press
            // gesture with react-sortable-hoc (mirrors the image branch, #21264).
            <video
              draggable={false}
              src={withVideoFirstFrame(getS3VideoUrl(file))}
              className={classes.thumbImg}
              aria-label={file.title || __('Video')}
              muted
              playsInline
              preload="metadata"
            />
          ) : file.cloudinaryPublicId ? (
            <CloudinaryImage
              publicId={`${file.cloudinaryPublicId}.jpg`}
              resourceType="video"
              crop="fill"
              width={isHorizontal ? 72 : 160}
              className={classes.thumbImg}
              alt={file.title || __('Video')}
            />
          ) : file.src ? (
            <video
              draggable={false}
              src={withVideoFirstFrame(file.src)}
              className={classes.thumbImg}
              aria-label={file.title || __('Video')}
              muted
              playsInline
              preload="metadata"
            />
          ) : (
            <InsertDriveFileOutlinedIcon fontSize="large" />
          )}
          <div className={classes.videoOverlay}>
            <PlayCircleFilledIcon />
          </div>
        </>
      ) : isPdf ? (
        <>
          <div className={classnames(classes.pdfPreviewSlot, isHorizontal && classes.pdfPreviewSlotHorizontal)}>
            {file.cloudinaryPublicId ? (
              <CloudinaryImage
                publicId={`${file.cloudinaryPublicId}.png`}
                page={1}
                resourceType="image"
                crop="fit"
                width={isHorizontal ? 50 : 64}
              />
            ) : (
              <InsertDriveFileOutlinedIcon fontSize="large" />
            )}
          </div>
          {!isHorizontal && (
            <>
              <div className={classnames(classes.docTitle, classes.pdfTitle)} title={file.title}>
                {file.title || __('File')}
              </div>
              <div className={classnames(classes.docFormat, classes.pdfBadge)}>
                <PictureAsPdfOutlinedIcon fontSize="small" />
                {__('PDF')}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <InsertDriveFileOutlinedIcon fontSize="large" />
          <div className={classes.docTitle} title={file.title}>
            {file.title || __('File')}
          </div>
          {file.format && <div className={classes.docFormat}>{file.format.split('/').pop()?.toUpperCase()}</div>}
        </>
      )}
      {uploading && (
        <div className={classes.spinnerOverlay}>
          <CircularProgress size={24} />
        </div>
      )}
      {errored && !uploading && (
        <div className={classnames(classes.spinnerOverlay, classes.errorOverlay)} title={file.error}>
          <ErrorOutlineIcon />
        </div>
      )}
    </button>
  )
}

// Typed exactly as FilesGridNext types its sortable parts: the second type arg
// is the HOC's internal state, which is opaque to us, hence `any`.
const SortableThumb: React.ComponentClass<ThumbButtonProps, any> = SortableElement(ThumbButton)

// Plain container the SortableContainer HOC drives. The upload + add-provider
// tiles are passed as trailing children (not SortableElements), so they stay
// put and never participate in the drag — matching how FilesGridNext keeps its
// upload dropzone non-sortable.
interface SortableGridProps extends SortableContainerProps {
  className: string
  children: React.ReactNode
}

const SortableGrid: React.ComponentClass<SortableGridProps, any> = SortableContainer(
  ({ className, children }: SortableGridProps) => <div className={className}>{children}</div>
)

function FileViewerGallery(props: FileViewerGalleryProps) {
  const {
    files,
    selectedKey,
    onSelect,
    onSelectAll,
    onUploadClick,
    onAddProvider,
    addProviderLabel,
    onReorder,
    selectedKeys,
    onToggleSelect,
    compactThreshold = 180,
    orientation = 'vertical'
  } = props
  const classes = useStyles({})
  const { __ } = useTranslation()
  const rootRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState<number>(280)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const isHorizontal = orientation === 'horizontal'
  const inMultiSelectMode = !!onToggleSelect && !!selectedKeys && selectedKeys.size > 0
  const canSelectAll = !!onSelectAll && files.length > 0

  useLayoutEffect(() => {
    if (!rootRef.current) return
    setWidth(rootRef.current.getBoundingClientRect().width)
  }, [])

  useEffect(() => {
    if (!rootRef.current || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) setWidth(entry.contentRect.width)
    })
    ro.observe(rootRef.current)
    return () => ro.disconnect()
  }, [])

  const compact = !isHorizontal && width < compactThreshold

  // Single source of truth for the upload affordance's wording. Drives the
  // visible label (non-compact) and the tooltip/accessible name in condensed
  // view (#21191). Drag-to-upload is handled by the viewer-wide drop zone in
  // FileViewer (#21418), so the tile is just a click affordance here.
  const uploadLabel = __('Upload a file')
  // Single resolved label for the add-provider affordance so the vertical tile
  // and the horizontal action bar share one default and the button always has an
  // accessible name even if the (optional) `addProviderLabel` is omitted.
  const providerLabel = addProviderLabel || __('Add photos')

  // Reorder is suppressed in multi-select mode so a checkbox tap-drag doesn't
  // start a sort. pressDelay (below) keeps quick taps as selection.
  const reorderDisabled = !onReorder || inMultiSelectMode
  const handleSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    if (!onReorder || oldIndex === newIndex) return
    onReorder(arrayMove(files, oldIndex, newIndex))
  }

  return (
    <div ref={rootRef} className={classnames(classes.root, isHorizontal && classes.rootHorizontal)}>
      <div className={classes.header}>
        <span className={classes.title}>{__('Files')}</span>
        {isHorizontal
          ? null
          : compact
            ? // Upload now lives in the always-visible tile (#21191), so the
              // compact kebab only needs to host "Select all" — and only when
              // that action exists.
              canSelectAll && (
                <>
                  <IconButton
                    size="small"
                    onClick={(e) => setMenuAnchor(e.currentTarget)}
                    aria-label={__('File actions')}
                  >
                    <MoreHorizIcon fontSize="small" />
                  </IconButton>
                  <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                    <MenuItem
                      onClick={() => {
                        onSelectAll?.()
                        setMenuAnchor(null)
                      }}
                    >
                      <ListItemIcon className={classes.menuItemIcon}>
                        <CheckBoxIcon fontSize="small" />
                      </ListItemIcon>
                      {__('Select all')}
                    </MenuItem>
                  </Menu>
                </>
              )
            : canSelectAll && (
                <FileViewerButton onClick={onSelectAll} tone="neutral">
                  {__('Select all')}
                </FileViewerButton>
              )}
      </div>
      <div className={classnames(classes.scroll, isHorizontal && classes.scrollHorizontal)}>
        <SortableGrid
          // axis matches the layout: 2-col grid drags freely (xy), the mobile
          // strip drags horizontally only.
          axis={isHorizontal ? 'x' : 'xy'}
          // A short press still registers as a click (select/navigate); holding
          // past the delay starts a drag. Matches FilesGridNext.
          pressDelay={150}
          helperClass={classes.dragging}
          onSortEnd={handleSortEnd}
          className={classnames(
            isHorizontal ? classes.strip : classes.grid,
            !isHorizontal && compact && classes.gridCompact
          )}
        >
          {files.map((file, index) => {
            const isMultiSelected = !!selectedKeys?.has(file.key)
            const selected = inMultiSelectMode ? isMultiSelected : file.key === selectedKey

            return (
              <SortableThumb
                key={file.key}
                index={index}
                disabled={reorderDisabled}
                file={file}
                isHorizontal={isHorizontal}
                selected={selected}
                inMultiSelectMode={inMultiSelectMode}
                isMultiSelected={isMultiSelected}
                onClick={() => (inMultiSelectMode && onToggleSelect ? onToggleSelect(file) : onSelect(file))}
              />
            )
          })}
          {onUploadClick && !isHorizontal && (
            // Stays visible even when the sidebar is fully condensed (#21191);
            // in compact mode the label is dropped so the icon-only tile fits
            // the narrow single column. The label still surfaces via the tooltip
            // and (since there's no visible text) the accessible name, so the
            // drag-state cue isn't lost in condensed view.
            <button
              type="button"
              // Only set an accessible name explicitly when there's no visible
              // label; in non-compact mode the visible <span> provides it and an
              // aria-label would just override identical text.
              aria-label={compact ? uploadLabel : undefined}
              title={uploadLabel}
              onClick={onUploadClick}
              className={classnames(classes.thumb, classes.uploadTile)}
            >
              <CloudUploadIcon className={classes.uploadIcon} />
              {!compact && <span className={classes.uploadLabel}>{uploadLabel}</span>}
            </button>
          )}
          {onAddProvider && !isHorizontal && (
            <button
              type="button"
              aria-label={compact ? providerLabel : undefined}
              title={providerLabel}
              onClick={onAddProvider}
              className={classnames(classes.thumb, classes.uploadTile)}
            >
              <AddPhotoAlternateOutlinedIcon className={classes.uploadIcon} />
              {!compact && <span className={classes.uploadLabel}>{providerLabel}</span>}
            </button>
          )}
        </SortableGrid>
      </div>
      {isHorizontal && (canSelectAll || onUploadClick || onAddProvider) && (
        // One horizontally-scrollable row. Order matters: Select all + Upload a
        // file come first so they're always visible, and "Add from CompanyCam"
        // (the longest label) sits last where it can scroll into view (#21231).
        <div className={classes.horizontalActions}>
          {canSelectAll && (
            <FileViewerButton onClick={onSelectAll} tone="neutral">
              <CheckBoxIcon fontSize="small" style={{ marginRight: 4 }} />
              {__('Select all')}
            </FileViewerButton>
          )}
          {onUploadClick && (
            <FileViewerButton onClick={onUploadClick} tone="neutral">
              <CloudUploadIcon fontSize="small" style={{ marginRight: 4 }} />
              {__('Upload a file')}
            </FileViewerButton>
          )}
          {onAddProvider && (
            <FileViewerButton onClick={onAddProvider} tone="neutral">
              <AddPhotoAlternateOutlinedIcon fontSize="small" style={{ marginRight: 4 }} />
              {providerLabel}
            </FileViewerButton>
          )}
        </div>
      )}
    </div>
  )
}

export default FileViewerGallery
